import { Schema as S } from "effect";

import { apiData } from "../../api/asyncData";
import { ApiKey } from "../../api/schemas/integrations";
import { RecordHistoryEntry } from "../../api/schemas/records";
import {
	CommentType,
	ScorecardConfig,
	ScorecardResult,
	Task,
	TaskComment,
} from "../../api/schemas/tasks";

export const Subtask = S.Struct({ task: Task, grade: S.NullOr(S.Number) });
export type Subtask = typeof Subtask.Type;

/**
 * Everything the read view shows, fetched together. Only the task itself is
 * required: a secondary part that failed is null (its panel says so), and
 * lookups that failed (configs, keys, people) are empty and fall back to
 * generic labels.
 */
export const Loaded = S.Struct({
	task: Task,
	parent: S.NullOr(S.Struct({ id: S.String, title: S.String })),
	comments: S.NullOr(S.Array(TaskComment)),
	subtasks: S.NullOr(S.Array(Subtask)),
	history: S.NullOr(S.Array(RecordHistoryEntry)),
	scorecard: S.NullOr(S.Array(ScorecardResult)),
	configs: S.Array(ScorecardConfig),
	/** Api keys, to name agent authors and assignees. */
	keys: S.Array(ApiKey),
	/** Record id to name, for `task.people`. */
	people: S.Record(S.String, S.String),
});
export type Loaded = typeof Loaded.Type;

export const Model = S.Struct({
	/** The task id from the route. */
	id: S.String,
	/** Live data (KTD8: tasks are not time-versioned, no asOf tag). */
	detail: apiData(Loaded).schema,
	/** Epoch ms read with the detail, for relative times. */
	now: S.Number,
	/**
	 * The Re-score write: running hides the button's label; every other
	 * non-idle state is an alert (AI off, AI not configured, the model call
	 * failed (502), or anything else).
	 */
	rescore: S.Literals([
		"idle",
		"running",
		"failed",
		"aiDisabled",
		"aiNotConfigured",
		"upstream",
	]),
	/** The comment composer; `replyTo` is the comment being answered. */
	draft: S.Struct({
		body: S.String,
		type: CommentType,
		replyTo: S.NullOr(S.String),
	}),
	/** A comment, promote or demote is in flight: one write at a time. */
	saving: S.Boolean,
	/** Why the last comment, promote or demote failed; `conflict` (409) also refetched. */
	writeError: S.NullOr(S.Literals(["conflict", "failed"])),
	/** Where the last write started ("composer", or the comment id) so its alert sits by that control. */
	writeAt: S.String,
});
export type Model = typeof Model.Type;

/** The composer textarea; Reply focuses it (and the browser scrolls it into view). */
export const COMPOSER_BODY_ID = "task-comment-body";
